import axios, { AxiosResponse } from 'axios';
import {
  encodeBase64,
  getGoogleRecaptchaToken,
  getLocaleCode,
  removeEmpty,
} from 'lib/utils';
import snakecaseKeys from 'snakecase-keys';

import {
  SubmitRequest,
  SubmitResponse,
  UploadCategory,
  UploadRequest,
  UploadResponse,
} from 'types/api';

export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_FORMS_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  responseType: 'json',
});

/**
 * Performs actual submission of the form
 *
 * @param formSessionUuid
 * @param formData
 * @param locale
 * @returns
 */
export const submitForm = async (
  formSessionUuid: string,
  formData: any,
  locale: string = 'en-US'
) => {
  const cleansedData = removeEmpty(formData);
  const recaptchaToken = await getGoogleRecaptchaToken();
  const submitResponse = await axiosInstance.post<
    SubmitRequest,
    AxiosResponse<SubmitResponse>
  >(
    '/v1/form/submit',
    {
      form_data: snakecaseKeys(cleansedData),
      form_session: formSessionUuid,
      locale: getLocaleCode(locale),
    },
    { headers: { 'recaptcha-token': recaptchaToken } }
  );
  return submitResponse.data;
};

/**
 * Performs the operations to encode the given File in base-64 and then upload it.
 *
 * @param file
 * @param formSessionUuid
 * @param category
 * @returns
 */
export const uploadFileV2 = async (
  file: File,
  formSessionUuid: string,
  category: UploadCategory
) => {
  const recaptchaToken = await getGoogleRecaptchaToken('UPLOAD');
  const base64Data = await encodeBase64(file);
  const uploadResponse = await axiosInstance.post<
    UploadRequest,
    AxiosResponse<UploadResponse>
  >(
    '/v2/document/upload',
    {
      category,
      content_type: file.type,
      file_content: base64Data,
      file_name: file.name,
      form_session: formSessionUuid,
    },
    { headers: { 'recaptcha-token': recaptchaToken } }
  );
  return uploadResponse.data;
};
